import React, { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import Layout from "../components/Layout";
import { Play, Lock, ArrowLeft, X } from "lucide-react";
import "../styles/creator-page.css";
import AgeGateGuard from "../components/AgeGateGuard";
import useScreenshotDeter from "../hooks/useScreenshotDeter";
import BlackoutCurtain from "../components/BlackoutCurtain";
import ForensicOverlay from "../components/ForensicOverlay";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function themeStyle(t) {
  if (!t) return {};
  return {
    "--c-bg": t.bg, "--c-surface": t.surface, "--c-surface-2": t.surface_2,
    "--c-text": t.text, "--c-text-soft": t.text_soft, "--c-accent": t.accent,
    "--c-on-accent": t.on_accent, "--c-border": t.border,
    "--c-heading-font": `'${t.heading_font}', serif`,
    "--c-heading-style": t.heading_style,
    "--c-body-font": `'${t.body_font}', sans-serif`,
    "--c-btn-radius": t.button_radius,
  };
}

function MediaCard({ item, theme, onOpen }) {
  const isVideo = item.media_type === "video";
  const src = (item.thumbnail_path || item.url || "").startsWith("http")
    ? (item.thumbnail_path || item.url)
    : (item.url || item.thumbnail_path);
  const locked = item.requires_tier && item.requires_tier !== "free";
  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      data-testid={`creator-gallery-item-${item.id}`}
      className="block relative aspect-[3/4] overflow-hidden c-card text-left"
      style={{ padding: 0 }}
    >
      {src ? (
        <img src={src} alt={item.caption || ""} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center" style={{ color: theme?.text_soft || "#999" }}>
          {isVideo ? <Play className="h-8 w-8" /> : "—"}
        </div>
      )}
      {locked && (
        <div className="absolute inset-0 backdrop-blur-md flex items-center justify-center bg-black/30">
          <Lock className="h-6 w-6 text-white" />
        </div>
      )}
      {isVideo && (
        <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-1.5 py-0.5">VIDEO</div>
      )}
    </button>
  );
}

function Lightbox({ item, theme, onClose, onPrev, onNext }) {
  const { t } = useTranslation();
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext]);

  const isVideo = item?.media_type === "video";
  const fullSrc = (item?.url || "").startsWith("http") ? item?.url : item?.url;
  const deter = useScreenshotDeter({ label: isVideo ? "paid-video" : "paid-image", enabled: !!item && !isVideo });

  if (!item) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4" onClick={onClose} data-testid="creator-gallery-lightbox">
      <button onClick={onClose} className="absolute top-4 right-4 text-white/80 hover:text-white" data-testid="lightbox-close">
        <X className="h-6 w-6" />
      </button>
      <div onClick={(e) => e.stopPropagation()} className="max-w-5xl max-h-[90vh] relative" ref={deter.ref}>
        {isVideo ? (
          <video src={fullSrc} controls autoPlay className="max-h-[85vh] max-w-full" />
        ) : (
          <img src={fullSrc} alt={item.caption || ""} className="max-h-[85vh] max-w-full object-contain select-none" draggable={false} />
        )}
        {!isVideo && <ForensicOverlay viewId={item.id} tag={item.owner_username} />}
        <BlackoutCurtain active={deter.blackout} testId="paid-blackout" message="Beskyttet betalt innhold — vises kun med aktivt fokus" />
        {item.caption && (
          <div className="text-center text-sm text-white/80 mt-3">{item.caption}</div>
        )}
      </div>
    </div>
  );
}

export default function SkaperGalleri() {
  return (
    <AgeGateGuard>
      <SkaperGalleriInner />
    </AgeGateGuard>
  );
}

function SkaperGalleriInner() {
  const { username } = useParams();
  const [params, setParams] = useSearchParams();
  const typeFilter = params.get("type") || "all"; // all | image | video
  const focusId = params.get("id");

  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openItem, setOpenItem] = useState(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/users/${username}/creator-page`).then((r) => r.data).catch(() => null),
      api.get(`/users/${username}/teasers?limit=200`).then((r) => r.data).catch(() => []),
    ]).then(([page, teasers]) => {
      setData(page);
      setItems(Array.isArray(teasers) ? teasers : []);
    }).finally(() => setLoading(false));
  }, [username]);

  // Åpne lightbox direkte hvis ?id= er gitt
  useEffect(() => {
    if (focusId && items.length) {
      const found = items.find((x) => x.id === focusId);
      if (found) setOpenItem(found);
    }
  }, [focusId, items]);

  const filtered = useMemo(() => {
    if (typeFilter === "image") return items.filter((x) => x.media_type !== "video");
    if (typeFilter === "video") return items.filter((x) => x.media_type === "video");
    return items;
  }, [items, typeFilter]);

  const t = data?.theme;
  const style = useMemo(() => themeStyle(t), [t]);

  const goPrev = () => {
    if (!openItem) return;
    const i = filtered.findIndex((x) => x.id === openItem.id);
    if (i > 0) setOpenItem(filtered[i - 1]);
  };
  const goNext = () => {
    if (!openItem) return;
    const i = filtered.findIndex((x) => x.id === openItem.id);
    if (i >= 0 && i < filtered.length - 1) setOpenItem(filtered[i + 1]);
  };

  if (loading) {
    return <Layout withSidebar={false}><div className="text-center text-[var(--cream-soft)] py-20">Laster…</div></Layout>;
  }

  if (!data?.user) {
    return (
      <Layout withSidebar={false} noVikingPattern>
        <div className="panel-tight p-12 text-center max-w-xl mx-auto">
          <h1 className="font-fraunces italic text-3xl text-[var(--cream)] mb-2">Skaper ikke funnet.</h1>
          <Link to="/skapere" className="text-[var(--magenta)] hover:underline">← Tilbake til skapere</Link>
        </div>
      </Layout>
    );
  }

  const u = data.user;

  const setType = (type) => {
    const next = new URLSearchParams(params);
    if (type === "all") next.delete("type");
    else next.set("type", type);
    next.delete("id");
    setParams(next);
  };

  return (
    <Layout withSidebar={false} noVikingPattern>
      <div className="creator-page-wrap" style={style} data-testid="creator-gallery-page" data-theme={t?.id}>
        <div className="max-w-screen-2xl mx-auto px-6 py-8">
          {/* Header med tilbake-lenke */}
          <div className="flex items-center gap-3 mb-6">
            <Link to={`/skaper/${u.username}`} className="c-btn c-btn-outline text-xs" data-testid="back-to-creator">
              <ArrowLeft className="h-4 w-4" /> {u.username}
            </Link>
            <h1 className="c-heading text-3xl md:text-4xl">Galleri</h1>
          </div>

          {/* Avatar + navn */}
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 border-2 overflow-hidden" style={{ borderColor: t?.accent, background: t?.surface_2 }}>
              {u.avatar_url ? (
                <img src={u.avatar_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center c-heading text-2xl">
                  {u.username.slice(0, 2).toUpperCase()}
                </div>
              )}
            </div>
            <div>
              <div className="c-heading text-2xl">{u.username}</div>
              {u.creator_tagline && <div className="c-tagline text-sm" style={{ opacity: 0.8 }}>{u.creator_tagline}</div>}
            </div>
          </div>

          {/* Filter-pills */}
          <div className="flex gap-2 mb-6" data-testid="gallery-type-filter">
            {[
              { v: "all", label: "Alle" },
              { v: "image", label: "Bilder" },
              { v: "video", label: "Film" },
            ].map((opt) => (
              <button
                key={opt.v}
                onClick={() => setType(opt.v)}
                data-testid={`gallery-filter-${opt.v}`}
                className={`px-4 py-1.5 text-xs font-meta tracking-[0.15em] border transition ${
                  typeFilter === opt.v
                    ? "bg-[var(--c-accent)] text-[var(--c-on-accent)] border-[var(--c-accent)]"
                    : "border-[var(--c-border)] text-[var(--c-text-soft)] hover:border-[var(--c-accent)]"
                }`}
              >
                {opt.label}
              </button>
            ))}
            <div className="ml-auto text-xs c-text-soft" style={{ color: t?.text_soft }}>
              {filtered.length} {filtered.length === 1 ? "element" : "elementer"}
            </div>
          </div>

          {/* Grid */}
          {filtered.length === 0 ? (
            <div className="c-card text-center" style={{ color: t?.text_soft }}>
              Ingen {typeFilter === "video" ? "filmer" : typeFilter === "image" ? "bilder" : "elementer"} enda.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="creator-gallery-grid">
              {filtered.map((item) => (
                <MediaCard key={item.id} item={item} theme={t} onOpen={setOpenItem} />
              ))}
            </div>
          )}
        </div>

        <Lightbox
          item={openItem}
          theme={t}
          onClose={() => setOpenItem(null)}
          onPrev={goPrev}
          onNext={goNext}
        />
      </div>
    </Layout>
  );
}
